import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage, formatDate } from "../Assets/assetsShared";
import "./LeaveApplication.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_SHORT = MONTH_NAMES.map((name) => name.slice(0, 3));

const TONE = {
  "Paid Leave": "casual",
  "Festival Leave": "emergency",
};

const emptyForm = () => ({
  leaveType: "Paid Leave",
  festival: "",
  duration: "Full Day",
  fromDate: "",
  toDate: "",
  reason: "",
});

const dateText = (leave) =>
  leave.halfDay ||
  String(leave.fromDate).slice(0, 10) === String(leave.toDate).slice(0, 10)
    ? `${formatDate(leave.fromDate)}${leave.halfDay ? " (Half day)" : ""}`
    : `${formatDate(leave.fromDate)} → ${formatDate(leave.toDate)}`;

// Live preview of the leave days (Sundays are not counted)
const previewDays = (from, to, half) => {
  if (!from) return 0;

  const start = new Date(`${from}T00:00:00.000Z`);

  if (half) return start.getUTCDay() === 0 ? 0 : 0.5;
  if (!to) return 0;

  const end = new Date(`${to}T00:00:00.000Z`);
  let days = 0;
  const cursor = new Date(start);

  while (cursor <= end) {
    if (cursor.getUTCDay() !== 0) days += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return days;
};

export default function LeaveApplication() {
  const navigate = useNavigate();

  // empty = let the server pick the latest year that has entries
  const [fy, setFy] = useState("");
  const [month, setMonth] = useState("all");
  const [data, setData] = useState(null);

  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ================= LOAD ================= */
  const load = async (nextFy = fy, nextMonth = month) => {
    try {
      const res = await api.get("/leaves/my", {
        params: { fy: nextFy || undefined, month: nextMonth },
      });

      setData(res.data);

      // keep the filters in sync with what really exists
      if (String(res.data.fy) !== String(nextFy)) setFy(String(res.data.fy));
      if (String(res.data.month) !== String(nextMonth)) {
        setMonth(String(res.data.month));
      }
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to load leaves."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(fy, month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fy, month]);

  /* ================= FORM ================= */
  const festivals = data?.festivals || [];
  const isFestival = form.leaveType === "Festival Leave";
  const selectedFestival = festivals.find((item) => item.name === form.festival);

  const half = form.duration === "Half Day";
  // single-day festivals and half days use one date only
  const singleDate =
    half || (isFestival && selectedFestival && selectedFestival.maxDays === 1);

  const effectiveTo = singleDate ? form.fromDate : form.toDate;
  const totalDays = previewDays(form.fromDate, effectiveTo, half);

  const setField = (field, value) =>
    setForm((current) => {
      const next = { ...current, [field]: value };

      if (field === "leaveType" && value !== "Festival Leave") {
        next.festival = "";
      }

      if (field === "fromDate" && next.toDate && next.toDate < value) {
        next.toDate = value;
      }

      return next;
    });

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await api.post("/leaves", {
        leaveType: form.leaveType,
        festival: isFestival ? form.festival : "",
        fromDate: form.fromDate,
        toDate: effectiveTo,
        halfDay: half,
        reason: form.reason,
      });

      setForm(emptyForm());
      setSuccess("Your leave request has been sent. Please wait for approval.");
      await load();
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to apply for leave."));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (leave) => {
    if (!window.confirm("Cancel this leave request?")) return;

    try {
      setError("");
      setSuccess("");
      await api.patch(`/leaves/${leave._id}/cancel`);
      await load();
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to cancel leave."));
    }
  };

  const leaves = data?.leaves || [];
  const summaries = data?.summaries || [];
  const balance = data?.balance || [];
  const typeEntries = Object.entries(data?.typeTotals || {}).filter(
    ([, days]) => days > 0
  );

  // financial years that have entries (fallback = the selected one)
  const fyOptions = data?.fyOptions?.length
    ? data.fyOptions
    : data
    ? [{ fy: data.fy, label: data.fyLabel }]
    : [];

  // totals of the selected financial year
  const current = summaries.find((item) => String(item.fy) === String(data?.fy)) || {
    taken: 0,
    pending: 0,
    rejected: 0,
  };

  return (
    <div className="lp-page">
      {/* ================= HERO: FINANCIAL YEAR FILTER + TOTAL ================= */}
      <div className="lp-hero">
        <div className="lp-hero-left">
          <h1>Leave Application</h1>
          <p>Request time off and track the status of your leaves.</p>
        </div>

        <div className="lp-hero-controls">
          <label className="lp-hero-filter">
            <span>Financial Year</span>
            <select
              value={fy}
              onChange={(e) => {
                setFy(e.target.value);
                setMonth("all");
              }}
            >
              {fyOptions.map((option) => (
                <option key={option.fy} value={option.fy}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {data?.canApprove && (
            <button
              type="button"
              className="lp-link-btn"
              onClick={() => navigate("/leave-management")}
            >
              Leave Management →
            </button>
          )}
        </div>

        <div className="lp-hero-stat">
          <div className="lp-hero-main">
            <strong>{current.taken}</strong>
            <span>
              {current.taken === 1 ? "day" : "days"} taken
              <small>
                {data ? `Apr ${data.fy} – Mar ${data.fy + 1}` : ""}
              </small>
            </span>
          </div>

          {month === "all" && typeEntries.length > 0 && (
            <div className="lp-hero-split">
              {typeEntries.map(([type, days]) => (
                <span key={type}>
                  {type}: {days}
                </span>
              ))}
            </div>
          )}

          <div className="lp-hero-mini">
            <span>
              Pending <b>{current.pending}</b>
            </span>
            <span>
              Rejected <b>{current.rejected}</b>
            </span>
          </div>
        </div>
      </div>

      {/* ================= LEAVE TAKEN (only types the user has taken) ================= */}
      {balance.some((item) => item.used > 0) && (
        <div className="lp-stats">
          {balance
            .filter((item) => item.used > 0)
            .map((item) => (
              <div
                key={item.type}
                className={`lp-bal lp-tone-${TONE[item.type] || "casual"}`}
              >
                <div className="lp-bal-body">
                  <h3>{item.type}</h3>
                  <span className="lp-taken-num">
                    {item.used}
                    <small>{item.used === 1 ? "day taken" : "days taken"}</small>
                  </span>
                </div>
              </div>
            ))}
        </div>
      )}

      <div className="lp-grid">
        {/* ================= APPLY ================= */}
        <div className="lp-card lp-form-card">
          <h2 className="lp-card-title">Apply for Leave</h2>

          {error && <div className="lp-alert lp-alert-error">{error}</div>}
          {success && <div className="lp-alert lp-alert-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <label className="lp-field">
              <span>Leave Type</span>
              <select
                required
                value={form.leaveType}
                onChange={(e) => setField("leaveType", e.target.value)}
              >
                {(data?.leaveTypes || ["Paid Leave", "Festival Leave"]).map(
                  (type) => (
                    <option key={type}>{type}</option>
                  )
                )}
              </select>
            </label>

            <p className="lp-hint">
              {isFestival
                ? "Festival leave is only for the listed festivals. Diwali can be up to 5 days."
                : "1 paid leave is credited every month (12 per year). Sick leave is taken from Paid Leave."}
            </p>

            {isFestival && (
              <label className="lp-field">
                <span>Festival</span>
                <select
                  required
                  value={form.festival}
                  onChange={(e) => setField("festival", e.target.value)}
                >
                  <option value="">Select festival</option>
                  {festivals.map((item) => (
                    <option
                      key={item.name}
                      value={item.name}
                      disabled={Boolean(item.status)}
                    >
                      {item.name}
                      {item.optional ? " (Optional)" : ""}
                      {item.maxDays > 1 ? ` (${item.maxDays} Days)` : ""}
                      {item.status ? ` - already ${item.status.toLowerCase()}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="lp-toggle">
              {["Full Day", "Half Day"].map((option) => (
                <button
                  type="button"
                  key={option}
                  className={form.duration === option ? "active" : ""}
                  onClick={() => setField("duration", option)}
                >
                  {option}
                </button>
              ))}
            </div>

            <div className="lp-dates">
              <label className="lp-field">
                <span>From Date</span>
                <input
                  required
                  type="date"
                  value={form.fromDate}
                  onChange={(e) => setField("fromDate", e.target.value)}
                />
              </label>

              <label className="lp-field">
                <span>To Date</span>
                <input
                  required={!singleDate}
                  disabled={singleDate}
                  type="date"
                  min={form.fromDate}
                  value={singleDate ? form.fromDate : form.toDate}
                  onChange={(e) => setField("toDate", e.target.value)}
                />
              </label>
            </div>

            <div className="lp-total">
              <span>Total leave days (Sundays excluded)</span>
              <strong>{totalDays}</strong>
            </div>

            <label className="lp-field">
              <span>Reason</span>
              <textarea
                required
                placeholder="Write the reason for your leave"
                value={form.reason}
                onChange={(e) => setField("reason", e.target.value)}
              />
            </label>

            <button type="submit" className="lp-submit" disabled={saving}>
              {saving ? "Submitting..." : "Submit Request"}
            </button>
          </form>
        </div>

        {/* ================= MY LEAVE REQUESTS ================= */}
        <div className="lp-card">
          <h2 className="lp-card-title">
            My Leave Requests
            {data?.fyLabel && <span className="lp-card-fy">{data.fyLabel}</span>}
          </h2>

          {loading && <div className="lp-empty">Loading...</div>}

          {!loading && data && !summaries.length && (
            <div className="lp-empty">You have not requested any leave yet.</div>
          )}

          {!loading && data && summaries.length > 0 && (
            <>
              <div className="lp-filters">
                <div className="lp-chips">
                  {typeEntries.length ? (
                    typeEntries.map(([type, days]) => (
                      <span key={type} className="lp-chip">
                        {type}: {days} days
                      </span>
                    ))
                  ) : (
                    <span className="lp-chip lp-chip-muted">
                      No approved leave in this period
                    </span>
                  )}
                </div>

                <label className="lp-month">
                  Month:
                  <select value={month} onChange={(e) => setMonth(e.target.value)}>
                    <option value="all">All Months</option>
                    {(data.monthOptions || []).map((value) => (
                      <option key={value} value={value}>
                        {MONTH_NAMES[value - 1]}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {!leaves.length ? (
                <div className="lp-empty">No leave requests in this period.</div>
              ) : (
                <div className="lp-list">
                  {leaves.map((leave) => {
                    const from = new Date(leave.fromDate);

                    return (
                      <div
                        key={leave._id}
                        className={`lp-item lp-item-${leave.status}`}
                      >
                        <div className="lp-date">
                          <strong>{from.getUTCDate()}</strong>
                          <small>{MONTH_SHORT[from.getUTCMonth()]}</small>
                        </div>

                        <div className="lp-item-main">
                          <h4>
                            {leave.leaveType}
                            {leave.festival ? ` · ${leave.festival}` : ""}
                          </h4>
                          <div className="lp-item-dates">{dateText(leave)}</div>
                          <p className="lp-item-reason">{leave.reason}</p>
                          <p className="lp-item-meta">
                            Applied on {formatDate(leave.createdAt)}
                            {leave.reviewedBy?.name &&
                              ` · ${leave.status} by ${leave.reviewedBy.name}`}
                            {leave.reviewNote && ` · “${leave.reviewNote}”`}
                          </p>
                        </div>

                        <div className="lp-item-side">
                          <span className="lp-days-pill">
                            {leave.days} {leave.days === 1 ? "day" : "days"}
                          </span>

                          <span className={`lp-badge lp-badge-${leave.status}`}>
                            {leave.status}
                          </span>

                          {leave.status === "Pending" && (
                            <button
                              type="button"
                              className="lp-cancel"
                              onClick={() => handleCancel(leave)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}